import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { EmptyState, PageContainer, PageHeader } from '../../components';
import { cx } from '../../lib/cx';
import { useRequiredWorkspace } from '../workspace/useWorkspace';
import { useCapability } from '../workspace/useCapability';
import { backlogQueryOptions } from '../backlog/api';
import { usePlanBoard } from './usePlanBoard';
import { PlanningView } from './PlanningView';
import { DeliveryBoard } from './DeliveryBoard';
import { SprintDrawer } from './SprintDrawer';

/**
 * Plan — one screen for what goes in a sprint and how that sprint is going.
 *
 * It replaces four routes. `/plan` and `/planning` were the same component
 * behind two names and two capability gates; `/sprints` showed the running
 * sprint that `/planning` deliberately hid, and linked back to it to place
 * work; `/board` showed that same sprint a third time, crossed with the
 * pipeline. Four destinations for two questions — what is committed, and how
 * is it moving — and answering either meant knowing which screen owned it.
 *
 * The two questions are now two LAYOUTS over one selection, so switching
 * between them keeps your place rather than navigating.
 *
 * Only one of the two is a client's, though. Delivery is the internal pipeline
 * and the request behind it needs `board.write`, so the toggle is hidden from
 * anyone who does not hold it — see `canSeeDelivery` below.
 */

type View = 'planning' | 'delivery';

/**
 * Edit is off by default, for everyone.
 *
 * Planning is read far more often than it is changed — standups, "when does
 * my thing land", a client checking the week — and a board whose cards move
 * under a stray touch is hostile to all of those. The choice is remembered
 * because the one person who does live in edit mode should not have to say so
 * every morning.
 */
const EDIT_KEY = 'rundown:plan:editing';

export function PlanPage() {
  const { workspace } = useRequiredWorkspace();
  const [params, setParams] = useSearchParams();
  const canCommit = useCapability('planning.write');
  const canSuggest = useCapability('plan.suggest');
  /**
   * Whether the Delivery layout exists at all for this person.
   *
   * `board.write`, because that is what BoardController requires of the request
   * behind it — one gate on the whole controller, since there is no "read the
   * pipeline but may not move a card" caller. A client was being offered the
   * toggle and then shown the 403 it produced: an option that is not an option,
   * which reads as the product being broken rather than as a boundary.
   *
   * What a client loses is nothing they were getting. The pipeline is internal
   * vocabulary (Triage, Staging) for the same progress their own status chip
   * already reports in the words §3 chose for them.
   */
  const canSeeDelivery = useCapability('board.write');

  const [pending, setPending] = useState<ReadonlyMap<string, string>>(new Map());
  const [showPast, setShowPast] = useState(false);
  const [editing, setEditing] = useState(() => localStorage.getItem(EDIT_KEY) === 'true');

  // A deep link into Delivery from someone who cannot load it lands on
  // Planning rather than on an error — `/board` still redirects here with
  // `view=delivery`, and those links were sent to everyone.
  const view: View = params.get('view') === 'delivery' && canSeeDelivery ? 'delivery' : 'planning';
  const selectedId = params.get('sprint');

  const board = usePlanBoard(workspace.id, pending, showPast);
  const backlogQuery = useQuery(backlogQueryOptions(workspace.id));

  useEffect(() => {
    localStorage.setItem(EDIT_KEY, String(editing));
  }, [editing]);

  const setView = (next: View) => {
    const updated = new URLSearchParams(params);
    if (next === 'planning') updated.delete('view');
    else updated.set('view', next);
    setParams(updated, { replace: true });
  };

  const select = (sprintId: string | null) => {
    const updated = new URLSearchParams(params);
    if (sprintId === null) updated.delete('sprint');
    else updated.set('sprint', sprintId);
    setParams(updated, { replace: true });
  };

  const selected =
    board.columns.find((column) => column.sprint.id === selectedId) ??
    // A deep link to a released sprint should open it, not silently show
    // nothing, so the past toggle follows the link rather than the other way
    // round.
    null;

  const canEdit = canCommit || canSuggest;

  return (
    <PageContainer width="full">
      <PageHeader
        title="Plan"
        description={
          view === 'planning'
            ? 'What is committed to each sprint, and what the ranking would pull in next. Open a sprint for its full scope.'
            : 'How this sprint’s work is moving. Drag a card to move it through the pipeline.'
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {canSeeDelivery && (
              <Switch
                options={[
                  { value: 'planning', label: 'Planning' },
                  { value: 'delivery', label: 'Delivery' },
                ]}
                value={view}
                onChange={(next) => setView(next as View)}
              />
            )}
            {canEdit && (
              <Switch
                options={[
                  { value: 'view', label: 'View' },
                  // Naming it for what it does: a client's drag is a
                  // suggestion, and calling that "Edit" would promise more
                  // than the server will honour.
                  { value: 'edit', label: canCommit ? 'Edit' : 'Suggest changes' },
                ]}
                value={editing ? 'edit' : 'view'}
                onChange={(next) => setEditing(next === 'edit')}
              />
            )}
          </div>
        }
      />

      {board.isPending ? (
        <p className="fs-12 text-muted">Loading…</p>
      ) : board.error !== null ? (
        <EmptyState title="Could not load the plan" description={board.error.message} />
      ) : view === 'planning' ? (
        <>
          <label className="mb-3 flex w-fit cursor-pointer items-center gap-1.5 fs-12 text-muted">
            <input
              type="checkbox"
              checked={showPast}
              onChange={(event) => setShowPast(event.target.checked)}
              className="accent-ink"
            />
            Show released sprints
          </label>
          <PlanningView
            workspaceId={workspace.id}
            board={board}
            editing={editing}
            pending={pending}
            setPending={setPending}
            selectedId={selectedId}
            onSelect={(sprintId) => select(sprintId === selectedId ? null : sprintId)}
          />
        </>
      ) : (
        <DeliveryBoard workspaceId={workspace.id} editing={editing} />
      )}

      {view === 'planning' && selected !== null && (
        <SprintDrawer
          workspaceId={workspace.id}
          column={selected}
          today={board.today}
          backlog={backlogQuery.data ?? []}
          onClose={() => select(null)}
        />
      )}
    </PageContainer>
  );
}

/** A two-or-three way segmented control, the shape both toggles here need. */
function Switch({
  options,
  value,
  onChange,
}: {
  readonly options: readonly { value: string; label: string }[];
  readonly value: string;
  readonly onChange: (next: string) => void;
}) {
  return (
    <div className="inline-flex rounded-control border border-line bg-surface-2 p-0.5">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          aria-pressed={value === option.value}
          className={cx(
            'rounded-[5px] px-2.5 py-1 fs-12 font-semibold whitespace-nowrap transition-colors',
            value === option.value ? 'bg-base text-ink shadow-sm' : 'text-muted hover:text-ink',
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
